import { collection, doc, getDoc, onSnapshot, query, serverTimestamp, setDoc, updateDoc, where, writeBatch } from "firebase/firestore";
import { auth, db } from "../firebase/firebase";
import { requireValidQuotePhoneVerification } from "./quotePhoneVerificationService";

export const TAILOR_QUOTE_STATUS = {
  AWAITING_TAILOR_QUOTE: "awaiting_tailor_quote",
  QUOTE_SENT_TO_CUSTOMER: "quote_sent_to_customer",
  CUSTOMER_ACCEPTED: "customer_accepted",
  PARTIAL_PAID: "partial_paid",
  AWAITING_FINAL_PAYMENT: "awaiting_final_payment",
  PAID: "paid",
  CANCELLED: "cancelled",
};

export function buildTailorQuotePayload({
  customer,
  garmentName,
  designImageDataUrl,
  designFileName,
  notes = "",
  draftId = "",
  serviceType = "my_design",
  garmentId = "",
  quotationType = serviceType,
  startingPrice = 0,
  requestDetails = {},
  sourceDesign = {},
  callbackPreference = "",
  callbackPreferenceLabel = "",
  preferredLanguage = "",
}) {
  const customerDetails = customer || {};
  const trimmedName = String(customerDetails.name || "").trim();
  const trimmedEmail = String(customerDetails.email || "").trim();
  const trimmedPhone = String(customerDetails.phone || "").trim();
  const trimmedGarmentName = String(garmentName || "").trim();

  return {
    requestId: "",
    customerId: auth.currentUser?.uid || customerDetails.uid || "",
    customerName: trimmedName,
    customerEmail: trimmedEmail,
    customerPhone: trimmedPhone,
    garmentName: trimmedGarmentName,
    garmentId: String(garmentId || ""),
    serviceType: String(serviceType || "my_design"),
    quotationType: String(quotationType || serviceType || "my_design"),
    draftId: String(draftId || ""),
    designImageDataUrl: String(designImageDataUrl || ""),
    designFileName: String(designFileName || ""),
    status: TAILOR_QUOTE_STATUS.AWAITING_TAILOR_QUOTE,
    quoteRequestedAt: new Date().toISOString(),
    quoteCurrency: "INR",
    finalQuoteAmount: null,
    startingPrice: Number(startingPrice || 0),
    requestDetails: requestDetails && typeof requestDetails === "object" ? requestDetails : {},
    sourceDesign: sourceDesign && typeof sourceDesign === "object" ? sourceDesign : {},
    callbackPreference: String(callbackPreference || ""),
    callbackPreferenceLabel: String(callbackPreferenceLabel || ""),
    preferredLanguage: String(preferredLanguage || ""),
    callbackDueAt: callbackPreference === "immediately"
      ? new Date().toISOString()
      : String(callbackPreference || ""),
    finalQuoteUpdatedAt: null,
    customerAcceptedQuote: false,
    customerAcceptedAt: null,
    customerNotes: String(notes || ""),
    tailorNotes: "",
    updatedAt: new Date().toISOString(),
  };
}

export async function submitTailorQuoteRequest({
  customer,
  garmentName,
  designImageDataUrl,
  designFileName,
  notes = "",
  draftId = "",
  serviceType = "my_design",
  garmentId = "",
  quotationType = serviceType,
  startingPrice = 0,
  requestDetails = {},
  sourceDesign = {},
  callbackPreference = "",
  callbackPreferenceLabel = "",
  preferredLanguage = "",
}) {
  const user = auth.currentUser;

  if (!user) {
    throw new Error("Please sign in to request a tailor quotation.");
  }

  if (serviceType === "my_design" && !designImageDataUrl) {
    throw new Error("A design image is required to request a tailor quotation.");
  }

  const normalizedPhone = String(customer?.phone || "").replace(/\D/g, "");
  const scheduledAt = Date.parse(callbackPreference);
  const now = Date.now();
  const validCallback = callbackPreference === "immediately" || (Number.isFinite(scheduledAt) && scheduledAt >= now - 60000 && scheduledAt < now + 3 * 60 * 60 * 1000);
  if (!/^[6-9]\d{9}$/.test(normalizedPhone) || !validCallback || !callbackPreferenceLabel || !["telugu", "hindi", "english"].includes(preferredLanguage)) {
    throw new Error("Enter a valid phone number, callback time and preferred language.");
  }
  const deviceInstallationId = await requireValidQuotePhoneVerification(normalizedPhone);

  const reference = doc(collection(db, "tailor_quote_requests"));
  const payload = buildTailorQuotePayload({
    customer: {
      ...customer,
      uid: user.uid,
      name: customer?.name || user.displayName || "",
      email: customer?.email || user.email || "",
      phone: customer?.phone || "",
    },
    garmentName,
    designImageDataUrl,
    designFileName,
    notes,
    draftId,
    serviceType,
    garmentId,
    quotationType,
    startingPrice,
    requestDetails,
    sourceDesign,
    callbackPreference,
    callbackPreferenceLabel,
    preferredLanguage,
  });

  const finalPayload = {
    ...payload,
    requestId: reference.id,
    customerId: user.uid,
    customerEmail: payload.customerEmail || user.email || "",
    customerName: payload.customerName || user.displayName || "",
    customerPhone: normalizedPhone,
    deviceInstallationId,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };

  await setDoc(reference, finalPayload);

  return {
    id: reference.id,
    ...finalPayload,
  };
}

export async function updateTailorQuoteRequest(requestId, updates = {}) {
  if (!requestId) {
    throw new Error("A request ID is required to update the tailor quote.");
  }

  const reference = doc(db, "tailor_quote_requests", requestId);
  await updateDoc(reference, {
    ...updates,
    updatedAt: serverTimestamp(),
  });

  return requestId;
}

export async function fetchTailorQuoteRequest(requestId) {
  if (!requestId) return null;

  const snapshot = await getDoc(doc(db, "tailor_quote_requests", requestId));
  if (!snapshot.exists()) return null;

  return { id: snapshot.id, ...snapshot.data() };
}

export function listenToTailorQuoteRequest(requestId, onSuccess, onError) {
  if (!requestId) return () => {};
  return onSnapshot(
    doc(db, "tailor_quote_requests", requestId),
    (snapshot) => onSuccess(snapshot.exists() ? { id: snapshot.id, ...snapshot.data() } : null),
    onError,
  );
}

export async function acceptTailorQuoteRequest(request) {
  const user = auth.currentUser;
  const amount = Number(request?.finalQuoteAmount || 0);
  if (!user || request?.customerId !== user.uid) {
    throw new Error("This quotation does not belong to your account.");
  }
  if (request.status !== TAILOR_QUOTE_STATUS.QUOTE_SENT_TO_CUSTOMER || amount <= 0) {
    throw new Error("Please wait until the tailor sends the final quotation.");
  }

  const requestId = request.id || request.requestId;
  const payAtVisit = ["stitching", "alteration"].includes(request.quotationType);
  const quoteDetails = request.quoteDetails && typeof request.quoteDetails === "object" ? request.quoteDetails : {};
  const sourceDesign = request.sourceDesign && typeof request.sourceDesign === "object" ? request.sourceDesign : {};
  const batch = writeBatch(db);
  batch.update(doc(db, "tailor_quote_requests", requestId), {
    customerAcceptedQuote: true,
    customerAcceptedAt: new Date().toISOString(),
    status: TAILOR_QUOTE_STATUS.CUSTOMER_ACCEPTED,
    updatedAt: serverTimestamp(),
  });
  batch.set(doc(db, "orders", requestId), {
    orderId: requestId,
    customerId: user.uid,
    userId: user.uid,
    customerName: request.customerName || user.displayName || "Customer",
    customerPhone: request.customerPhone || "",
    customerEmail: request.customerEmail || user.email || "",
    status: "pending",
    orderStatus: "pending",
    paymentStatus: payAtVisit ? "due_at_measurement_visit" : "pending",
    quoteRequestId: requestId,
    serviceType: request.serviceType || request.quotationType || "stitching",
    workflowType: request.serviceType || request.quotationType || "stitching",
    garment: {
      id: request.garmentId || "",
      name: request.garmentName || "Tailoring service",
      image: request.designImageUrl || request.designImageDataUrl || "",
      source: "custom_quote_request",
    },
    design: {
      ...sourceDesign,
      source: "custom_quote_request",
      imageUrl: request.designImageUrl || request.designImageDataUrl || "",
    },
    measurements: quoteDetails.measurements?.details || {},
    fabric: quoteDetails.fabric?.details || {},
    addOns: quoteDetails.addOns?.details || {},
    pickup: request.requestDetails?.pickup || {},
    delivery: request.requestDetails?.delivery || {},
    basePrice: amount,
    total: amount,
    pricing: {
      grandTotal: amount,
      advanceAmount: payAtVisit ? 0 : amount * 0.2,
      remainingAmount: payAtVisit ? amount : amount * 0.8,
    },
    source: "tailor_quote_request",
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  }, { merge: true });
  await batch.commit();
  return requestId;
}

export async function rejectTailorQuoteRequest(request) {
  const user = auth.currentUser;
  if (!user || request?.customerId !== user.uid) {
    throw new Error("This quotation does not belong to your account.");
  }
  if (request.status !== TAILOR_QUOTE_STATUS.QUOTE_SENT_TO_CUSTOMER) {
    throw new Error("Only a ready quotation can be rejected.");
  }
  await updateDoc(doc(db, "tailor_quote_requests", request.id || request.requestId), {
    customerAcceptedQuote: false,
    status: TAILOR_QUOTE_STATUS.CANCELLED,
    paymentStatus: "cancelled",
    updatedAt: serverTimestamp(),
  });
}

export function listenToCustomerTailorQuotes(customerId, onSuccess, onError) {
  if (!customerId) return () => {};
  return onSnapshot(
    query(collection(db, "tailor_quote_requests"), where("customerId", "==", customerId)),
    (snapshot) => {
      const requests = snapshot.docs.map((entry) => ({
        id: entry.id,
        ...entry.data(),
        recordType: "tailor_quote",
      })).sort((a, b) => {
        const time = (value) => value.quoteRequestedAt?.toDate?.()?.getTime?.() || Date.parse(value.quoteRequestedAt || value.createdAt || 0) || 0;
        return time(b) - time(a);
      });
      onSuccess(requests);
    },
    onError,
  );
}

export const tailorQuoteStatusLabel = (status) => ({
  awaiting_tailor_quote: "Quote requested",
  quote_sent_to_customer: "Awaiting your response",
  customer_accepted: "Quote accepted",
  partial_paid: "Partially paid",
  awaiting_final_payment: "Awaiting final payment",
  paid: "Completed",
  cancelled: "Cancelled",
}[status] || String(status || "Quote requested").replaceAll("_", " "));
